import { Fragment, useEffect, useState } from 'react';
import { api, slotLabel, type CoverageForecast as Forecast, type PhysicianSummary } from '../lib/api';
import { dayLabel } from '../lib/dates';
import { navigate } from '../lib/router';

/**
 * Can we cover this quarter, and what is missing? (D42, step 1.)
 *
 * Built before any automatic drafting, because it answers the office's
 * question on its own: for every hospital position and every week, how
 * many credentialed physicians are free — and which gaps a person has to
 * close before a schedule can be built at all. It decides nothing and
 * proposes nobody; it counts.
 *
 * The one thing it is careful about: a physician who has said nothing is
 * not a physician who said yes. The number counts everyone who has not
 * said no; the colour only reads "fine" once somebody has actually told
 * us, and says "not answered" until then.
 */
export function CoverageForecast({ quarter }: { quarter: string }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<Forecast | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let ignore = false;
    setData(null);
    setError(null);
    api
      .coverageForecast(quarter)
      .then((d) => { if (!ignore && d.quarter === quarter) setData(d); })
      .catch((e: Error) => { if (!ignore) setError(e.message); });
    return () => { ignore = true; };
  }, [open, quarter]);

  const g = data?.gaps;
  const gapCount = g ? g.noCredentialing.length + g.backupPrecondition.length + g.nobodyCredentialed.length + g.thinPools.length + g.neverScheduled.length : 0;

  return (
    <section className="card forecast">
      <h2>
        <button type="button" className="disclosure" aria-expanded={open} aria-controls="forecast-body" onClick={() => setOpen((v) => !v)}>
          <span className="tri" aria-hidden="true">{open ? '▾' : '▸'}</span> Can we cover this quarter?
        </button>
      </h2>
      <div id="forecast-body">
        {!open && <p className="muted small">Who is credentialed and free for every position, week by week — and what has to be fixed before a schedule can be built.</p>}
        {open && error && <p className="error">Couldn't work that out ({error}).</p>}
        {open && !data && !error && <p className="muted">Counting…</p>}
        {open && data && (
          <>
            <p className="forecast-head">
              <strong>{data.responses.answered} of {data.responses.physicians}</strong> physicians have answered
              {data.responses.answered > 0 && <> · {data.responses.withoutTarget} of those didn't say how many weeks they want · {data.responses.withoutBlockLength} didn't say how long a block</>}
            </p>

            {gapCount === 0 ? (
              <p className="ok small">Nothing is missing: every position that is expected to be staffed has credentialed physicians, and every hospital has run before.</p>
            ) : (
              <div className="gaps">
                <h3>Before a quarter can be built</h3>
                <ul>
                  {g!.noCredentialing.length > 0 && (
                    <li>
                      <strong>{g!.noCredentialing.length} {g!.noCredentialing.length === 1 ? 'physician is' : 'physicians are'} credentialed nowhere</strong> — they can't be scheduled at any hospital until the Roster page says where they're credentialed: <Names people={g!.noCredentialing} full />
                    </li>
                  )}
                  {g!.nobodyCredentialed.map((n) => (
                    <li key={`${n.siteId}|${n.positionSlot}`}>
                      <strong>{n.siteName} {slotLabel(n.positionSlot)}</strong> — nobody is credentialed for it{n.alsoRequires ? ` (and for ${slotLabel(n.alsoRequires)}, which the week also hands them)` : ''}. It can't be filled at all until somebody is.
                    </li>
                  ))}
                  {g!.backupPrecondition.map((b) => (
                    <li key={b.siteId}>
                      <strong>{b.siteName}</strong> — {b.physicians.length} credentialed {b.physicians.length === 1 ? 'physician cannot' : 'physicians cannot'} hold any of {b.positions.map(slotLabel).join(', ')}, because whoever holds one is also handed {b.backupSlot ? slotLabel(b.backupSlot) : 'backup call'}, which they aren't credentialed for: <Names people={b.physicians} full />
                    </li>
                  ))}
                  {g!.thinPools.map((t) => (
                    <li key={`${t.siteId}|${t.positionSlot}`}>
                      <strong>{t.siteName} {slotLabel(t.positionSlot)}</strong> has only {t.credentialed} credentialed {t.credentialed === 1 ? 'physician' : 'physicians'} for {t.weeksNeeded} weeks — one holiday leaves it open.
                    </li>
                  ))}
                  {g!.neverScheduled.map((s) => (
                    <li key={s.siteId}><strong>{s.siteName}</strong> has never been scheduled on the platform, but its positions are expected to be staffed. Confirm it's covered this quarter.</li>
                  ))}
                </ul>
              </div>
            )}

            {data.notes.length > 0 && (
              <div className="gaps">
                <h3>Read before building</h3>
                <ul>{data.notes.map((n) => <li key={n.abbreviation}><strong>{n.firstName} {n.lastName}</strong>: {n.note}</li>)}</ul>
              </div>
            )}

            <h3>Who is free, week by week</h3>
            <p className="muted small">
              The number is how many credentialed physicians haven't said they're unavailable — it does not mean they're assigned, only that they could be.
              {' '}<span className="key sup-none">red</span> nobody can ·{' '}
              <span className="key sup-fragile">amber</span> one deep ·{' '}
              <span className="key sup-unknown">grey</span> nobody free has answered yet — the people who could work it have said nothing, which is a phone call rather than a gap.
              The number counts only physicians who answered and did not rule the week out, because that is exactly who the drafter will offer it to: silence is not availability.
              A dot marks a week somebody asked for.
            </p>
            <div className="gridscroll">
              <table className="grid forecast-table">
                <thead>
                  <tr>
                    <th className="sticky name">Position</th>
                    {data.weeks.map((w) => <th key={w} scope="col">{dayLabel(w).replace(/^\w+, /, '').replace(/, \d{4}$/, '')}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.sites.map((site) => (
                    <Fragment key={site.siteId}>
                      <tr className="sitehead">
                        <th className="sticky name" colSpan={data.weeks.length + 1} scope="colgroup">
                          {site.siteName}
                          {!site.coverageConfigured && <span className="muted small"> · no coverage expectation configured</span>}
                          {site.neverScheduled && <span className="muted small"> · never scheduled here</span>}
                        </th>
                      </tr>
                      {site.positions.map((p) => (
                        <tr key={`${site.siteId}|${p.positionSlot}`}>
                          <th className="sticky name" scope="row">
                            {slotLabel(p.positionSlot)}
                            <span className="muted small">
                              {' · '}
                              {p.provisional === p.credentialed && p.credentialed > 0
                                ? `${p.credentialed} credentialed, all still in process`
                                : `${p.credentialed} credentialed${p.provisional > 0 ? `, ${p.provisional} in process` : ''}`}
                              {!p.expected && ' · not in the coverage plan'}
                            </span>
                          </th>
                          {p.weeks.map((w) => (
                            <td
                              key={w.week}
                              className={`cell sup-${w.tone}`}
                              title={`${w.free} answered and are free${w.silent ? `; ${w.silent} have not answered, so they do not count` : ''}${w.wanted ? `; ${w.wanted} asked for this week` : ''}`}
                            >
                              {w.free}
                              {w.wanted > 0 && <span className="want-dot" aria-hidden="true">·</span>}
                              <span className="sr-only">{`, ${w.silent} have not answered`}</span>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function Names({ people, full }: { people: PhysicianSummary[]; full?: boolean }) {
  return (
    <>
      {people.map((p, i) => (
        <span key={p.abbreviation}>
          {i > 0 && ', '}
          <button type="button" className="linklike" onClick={() => navigate(`/admin/roster/${encodeURIComponent(p.abbreviation)}`)}>
            {full ? `${p.firstName} ${p.lastName}` : p.lastName}
          </button>
        </span>
      ))}
    </>
  );
}
